import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Plus, Wallet, Save, Users, Check, Pencil, FolderKanban } from "lucide-react";
import { BudgetPeriodsEditor } from "@/pages/employee/pnl/BudgetPeriodsEditor";
import { ProjectListPanel } from "@/pages/employee/projects/ProjectListPanel";
import { ProjectDetailPanel } from "@/pages/employee/projects/ProjectDetailPanel";
import { AssignmentSheet } from "@/pages/employee/projects/AssignmentSheet";
import { AddMembersSheet } from "@/pages/employee/projects/AddMembersSheet";
import { loadByEmployee } from "@/pages/employee/projects/allocUtils";

export default function Assignments() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [pnlById, setPnlById] = useState({});
  const [loading, setLoading] = useState(true);
  const [projectSearch, setProjectSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [sheetAsg, setSheetAsg] = useState(null);
  const [addFor, setAddFor] = useState(null);

  // Financial editor state
  const [finProject, setFinProject] = useState(null);
  const [finForm, setFinForm] = useState({ budget_hours: 0, direct_expenses: 0, indirect_expenses_pct: "" });
  const [finBusy, setFinBusy] = useState(false);

  // New project state
  const [newProject, setNewProject] = useState(false);
  const [projBusy, setProjBusy] = useState(false);
  const emptyProj = { name: "", code: "", saga_code: "", client_name: "", location: "", sector: "", description: "", status: "planning", start_date: "", end_date: "", budget_amount: 0, budget_hours: 0 };
  const [projForm, setProjForm] = useState(emptyProj);
  // Team allocation at project creation
  const [projTeamMode, setProjTeamMode] = useState("none"); // none | all | some
  const [projTeamSelected, setProjTeamSelected] = useState(new Set());
  const [projTeamHours, setProjTeamHours] = useState(100);
  const [projTeamStart, setProjTeamStart] = useState("");
  const [projTeamEnd, setProjTeamEnd] = useState("");
  const [projTeamPermanent, setProjTeamPermanent] = useState(false);

  // Edit project state
  const [editProject, setEditProject] = useState(null);
  const [editForm, setEditForm] = useState(emptyProj);
  const [editBusy, setEditBusy] = useState(false);

  const canManageProjects = hasPermission("projects.manage");
  const canSeePnl = hasPermission("reports.view");

  const asgByProject = useMemo(() => {
    const m = {};
    for (const a of assignments) (m[a.project_id] ||= []).push(a);
    return m;
  }, [assignments]);
  const empLoad = useMemo(() => loadByEmployee(assignments), [assignments]);
  const overByProject = useMemo(() => {
    const m = {};
    for (const a of assignments) if ((empLoad[a.employee_id] || 0) > 100) m[a.project_id] = (m[a.project_id] || 0) + 1;
    return m;
  }, [assignments, empLoad]);
  const overTotal = Object.values(empLoad).filter((v) => v > 100).length;

  const visible = useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    return projects.filter((p) => (statusFilter === "all" || p.status === statusFilter) && (!q ||
      [p.name, p.code, p.saga_code, p.client_name].some((v) => (v || "").toLowerCase().includes(q))));
  }, [projects, projectSearch, statusFilter]);
  const selected = projects.find((p) => p.id === selectedId) || null;

  async function loadPnl() {
    if (!canSeePnl) return;
    try {
      const { data } = await api.get("/pnl/overview");
      setPnlById(Object.fromEntries((data.projects || []).map((x) => [x.project_id, x])));
    } catch (_) { /* P&L opțional */ }
  }

  async function load() {
    try {
      const [p, a] = await Promise.all([
        api.get("/projects"),
        api.get("/staff/assignments"),
      ]);
      setProjects(p.data);
      setAssignments(a.data);
      setSelectedId((cur) => (cur && p.data.some((x) => x.id === cur) ? cur : (window.innerWidth >= 1024 && p.data[0]?.id) || null));
      if (hasPermission("users.manage") || hasPermission("assignments.manage") || hasPermission("projects.manage")) {
        try { const u = await api.get("/staff/assignable-users"); setEmployees(u.data); }
        catch (_) { /* not authorized — ok */ }
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setLoading(false); }
    loadPnl();
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function refreshAssignments() {
    try { const a = await api.get("/staff/assignments"); setAssignments(a.data); }
    catch (e) { toast.error(apiErr(e)); }
    loadPnl();
  }

  async function removeProject(p) {
    if (!window.confirm(`Sigur ștergi proiectul „${p.name}"? Se vor șterge și alocările și jaloanele asociate. Acțiunea este ireversibilă.`)) return;
    try {
      await api.delete(`/staff/projects/${p.id}`);
      toast.success("Proiect șters");
      setSelectedId(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
  }

  async function createProject() {
    if (!projForm.name.trim() || !projForm.code.trim()) return toast.error("Completează numele și codul proiectului");
    if (projTeamMode === "some" && projTeamSelected.size === 0)
      return toast.error("Selectează cel puțin o persoană sau schimbă modul de alocare");
    setProjBusy(true);
    try {
      const res = await api.post("/staff/projects", {
        name: projForm.name.trim(),
        code: projForm.code.trim(),
        saga_code: projForm.saga_code || "",
        client_name: projForm.client_name || "",
        location: projForm.location,
        sector: projForm.sector,
        description: projForm.description,
        status: projForm.status,
        start_date: projForm.start_date || null,
        end_date: projForm.end_date || null,
        budget_amount: parseFloat(projForm.budget_amount) || 0,
        budget_hours: parseFloat(projForm.budget_hours) || 0,
      });
      const newId = res.data?.id;
      let allocMsg = "";
      if (newId && projTeamMode !== "none") {
        try {
          const r = await api.post("/staff/assignments/bulk", {
            project_id: newId,
            employee_ids: projTeamMode === "some" ? [...projTeamSelected] : [],
            all_employees: projTeamMode === "all",
            allocated_hours: parseFloat(projTeamHours) || 0,
            start_date: projTeamPermanent ? null : (projTeamStart || null),
            end_date: projTeamPermanent ? null : (projTeamEnd || null),
            permanent: projTeamPermanent,
          });
          const c = r.data?.created || 0;
          allocMsg = c ? ` · ${c} ${c === 1 ? "angajat alocat" : "angajați alocați"}` : "";
        } catch (e) { toast.error(apiErr(e)); }
      }
      toast.success(`Proiect creat${allocMsg}`);
      setNewProject(false);
      setProjForm(emptyProj);
      setProjTeamMode("none");
      setProjTeamSelected(new Set());
      setProjTeamHours(100);
      setProjTeamStart("");
      setProjTeamEnd("");
      setProjTeamPermanent(false);
      if (newId) setSelectedId(newId);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setProjBusy(false); }
  }

  async function saveAssignment(id, patch) {
    try {
      const { data } = await api.put(`/staff/assignments/${id}`, patch);
      setAssignments((prev) => prev.map((a) => (a.id === data.id ? data : a)));
      toast.success("Alocarea a fost salvată");
      loadPnl();
      return true;
    } catch (e) {
      toast.error(apiErr(e));
      return false;
    }
  }

  async function removeAssignment(id) {
    if (!window.confirm("Sigur dezaloci acest angajat?")) return;
    try {
      await api.delete(`/staff/assignments/${id}`);
      setAssignments((list) => list.filter((a) => a.id !== id));
      setSheetAsg(null);
      toast.success("Angajat dezalocat");
      loadPnl();
    } catch (e) { toast.error(apiErr(e)); }
  }

  async function setProjectManager(projectId, pmId) {
    try {
      await api.put(`/staff/projects/${projectId}/manager`, { project_manager_id: pmId || null });
      setProjects((prev) => prev.map((p) => (p.id === projectId ? { ...p, project_manager_id: pmId || null } : p)));
      toast.success("Project Manager actualizat");
    } catch (e) { toast.error(apiErr(e)); }
  }

  function openEdit(p) {
    setEditForm({
      name: p.name || "", code: p.code || "", saga_code: p.saga_code || "", client_name: p.client_name || "",
      location: p.location || "", sector: p.sector || "", description: p.description || "",
      status: p.status || "planning", start_date: p.start_date || "", end_date: p.end_date || "",
      budget_amount: p.budget_amount || 0, budget_hours: p.budget_hours || 0,
      budget_periods: (p.budget_periods || []).map((bp) => ({ ...bp })),
    });
    setEditProject(p);
  }

  async function saveEdit() {
    if (!editForm.name.trim() || !editForm.code.trim()) return toast.error("Completează numele și codul proiectului");
    const periods = editForm.budget_periods || [];
    for (const bp of periods) {
      if (!bp.start_date || !bp.end_date) return toast.error("Fiecare perioadă de buget are nevoie de dată de început și de sfârșit");
      if (bp.end_date < bp.start_date) return toast.error("Data de sfârșit a perioadei trebuie să fie după data de început");
    }
    setEditBusy(true);
    try {
      const body = {
        name: editForm.name.trim(),
        code: editForm.code.trim(),
        saga_code: editForm.saga_code || "",
        client_name: editForm.client_name || "",
        location: editForm.location,
        sector: editForm.sector,
        description: editForm.description,
        status: editForm.status,
        start_date: editForm.start_date || null,
        end_date: editForm.end_date || null,
        budget_hours: parseFloat(editForm.budget_hours) || 0,
      };
      if (periods.length === 0) body.budget_amount = parseFloat(editForm.budget_amount) || 0;
      await api.put(`/staff/projects/${editProject.id}`, body);
      await api.put(`/pnl/projects/${editProject.id}/budget-periods`, {
        periods: periods.map((bp) => ({ start_date: bp.start_date, end_date: bp.end_date, budget_amount: parseFloat(bp.budget_amount) || 0, label: bp.label || "" })),
      });
      toast.success("Proiect actualizat");
      setEditProject(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setEditBusy(false); }
  }

  function openFinancials(p) {
    setFinProject(p);
    setFinForm({
      budget_hours: p.budget_hours || 0,
      direct_expenses: p.direct_expenses || 0,
      indirect_expenses_pct: p.indirect_expenses_pct == null ? "" : p.indirect_expenses_pct,
    });
  }

  async function saveFinancials() {
    setFinBusy(true);
    try {
      await api.put(`/staff/projects/${finProject.id}/financials`, {
        budget_hours: parseFloat(finForm.budget_hours) || 0,
        direct_expenses: parseFloat(finForm.direct_expenses) || 0,
        indirect_expenses_pct: finForm.indirect_expenses_pct === "" ? null : (parseFloat(finForm.indirect_expenses_pct) || 0),
      });
      toast.success("Bugetul proiectului actualizat");
      setFinProject(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setFinBusy(false); }
  }

  const openNew = () => { setProjForm(emptyProj); setProjTeamMode("none"); setProjTeamSelected(new Set()); setProjTeamHours(100); setNewProject(true); };

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1440px]">
        <div className="mb-6 md:mb-8">
          <div className="font-mono text-[11px] uppercase tracking-widest text-ink-muted mb-2">Proiecte & alocări</div>
          <h1 className="font-display font-bold text-3xl md:text-[36px] tracking-tight leading-tight text-ink">Echipe pe proiect</h1>
          <p className="mt-2 text-sm text-ink-muted max-w-2xl">Alege un proiect ca să-i vezi echipa, încărcarea fiecărui om și consumul de buget. Alocările se editează din panoul fiecărui angajat.</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-ink-muted">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Se încarcă...
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <aside className={`lg:col-span-4 xl:col-span-4 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1 ${selected ? "hidden lg:block" : ""}`}>
              <ProjectListPanel projects={projects} visible={visible} asgByProject={asgByProject} pnlById={pnlById} load={canSeePnl}
                overByProject={overByProject} overTotal={overTotal} search={projectSearch} setSearch={setProjectSearch}
                filter={statusFilter} setFilter={setStatusFilter} selectedId={selectedId} onSelect={setSelectedId}
                canCreate={canManageProjects} onNew={openNew} />
            </aside>
            <section className={`lg:col-span-8 xl:col-span-8 ${selected ? "" : "hidden lg:block"}`}>
              {selected ? (
                <ProjectDetailPanel p={selected} rows={asgByProject[selected.id] || []} pnl={canSeePnl ? pnlById[selected.id] || null : undefined}
                  load={empLoad} overCount={overByProject[selected.id] || 0}
                  perms={{ pnl: canSeePnl, client: hasPermission("client.manage"), manage: canManageProjects }}
                  onBack={() => setSelectedId(null)}
                  actions={{
                    pnl: () => navigate(`/staff/bugete?project=${selected.id}`),
                    client: () => navigate(`/staff/client/${selected.id}`),
                    edit: () => openEdit(selected), fin: () => openFinancials(selected), remove: () => removeProject(selected),
                    setPm: (id) => setProjectManager(selected.id, id), addMembers: () => setAddFor(selected),
                    openMember: (a) => setSheetAsg(a),
                  }} />
              ) : (
                <div className="bg-white border border-dashed border-neutral-200 rounded-2xl py-24 text-center" data-testid="detail-empty">
                  <FolderKanban className="h-8 w-8 mx-auto text-neutral-300" />
                  <div className="mt-3 font-display font-semibold text-ink">Alege un proiect din listă</div>
                  <div className="text-sm text-ink-muted">Vei vedea echipa, alocările și bugetul lui.</div>
                </div>
              )}
            </section>
          </div>
        )}
      </div>

      <AssignmentSheet assignment={sheetAsg} project={projects.find((p) => p.id === sheetAsg?.project_id)}
        totalLoad={sheetAsg ? empLoad[sheetAsg.employee_id] || 0 : 0}
        onClose={() => setSheetAsg(null)} onSave={saveAssignment} onRemove={removeAssignment} />
      <AddMembersSheet project={addFor} employees={employees} load={empLoad}
        assignedIds={new Set((asgByProject[addFor?.id] || []).map((a) => a.employee_id))}
        onClose={() => setAddFor(null)} onDone={refreshAssignments} />

      {/* New Project Modal */}
      <Dialog open={newProject} onOpenChange={(o) => !o && setNewProject(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
              <Plus className="h-6 w-6 text-brand" /> Proiect nou
            </DialogTitle>
            <DialogDescription>
              Creează un proiect nou. Poți aloca echipa și configura bugetul după creare.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <ProjField label="Nume proiect *" value={projForm.name} onChange={(v) => setProjForm({ ...projForm, name: v })} testid="proj-name" />
            <ProjField label="Cod proiect *" value={projForm.code} onChange={(v) => setProjForm({ ...projForm, code: v })} testid="proj-code" placeholder="BRP-2026-001" />
            <ProjField label="Cod SAGA" value={projForm.saga_code} onChange={(v) => setProjForm({ ...projForm, saga_code: v })} testid="proj-saga" placeholder="Cod contabil SAGA (doar în export)" />
            <ProjField label="Client" value={projForm.client_name} onChange={(v) => setProjForm({ ...projForm, client_name: v })} testid="proj-client" placeholder="Nume client" />
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Status</label>
              <select data-testid="proj-status" value={projForm.status}
                onChange={(e) => setProjForm({ ...projForm, status: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 bg-white focus:border-brand focus:outline-none text-sm">
                <option value="planning">Planificare</option>
                <option value="active">Activ</option>
                <option value="in_progress">În desfășurare</option>
                <option value="completed">Finalizat</option>
              </select>
            </div>
            <ProjField label="Locație" value={projForm.location} onChange={(v) => setProjForm({ ...projForm, location: v })} testid="proj-location" />
            <ProjField label="Sector" value={projForm.sector} onChange={(v) => setProjForm({ ...projForm, sector: v })} testid="proj-sector" placeholder="Rezidențial, Office..." />
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Data început</label>
              <input data-testid="proj-start" type="date" value={projForm.start_date}
                onChange={(e) => setProjForm({ ...projForm, start_date: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Data sfârșit</label>
              <input data-testid="proj-end" type="date" value={projForm.end_date}
                onChange={(e) => setProjForm({ ...projForm, end_date: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm" />
            </div>
            <ProjField label="Buget total (€)" type="number" value={projForm.budget_amount} onChange={(v) => setProjForm({ ...projForm, budget_amount: v })} testid="proj-budget" />
            <ProjField label="Buget ore (h)" type="number" value={projForm.budget_hours} onChange={(v) => setProjForm({ ...projForm, budget_hours: v })} testid="proj-hours" />
            <div className="col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Descriere</label>
              <textarea data-testid="proj-desc" rows={3} value={projForm.description}
                onChange={(e) => setProjForm({ ...projForm, description: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm" />
            </div>
          </div>

          {/* Team allocation */}
          <div className="mt-5 border-t border-neutral-100 pt-5">
            <div className="flex items-center gap-2 mb-3">
              <Users className="h-4 w-4 text-brand" />
              <div className="font-display font-semibold text-sm text-ink">Alocă echipa</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { k: "none", t: "Nu aloca acum" },
                { k: "all", t: "Toți angajații" },
                { k: "some", t: "Anumite persoane" },
              ].map((o) => (
                <button
                  key={o.k}
                  type="button"
                  data-testid={`proj-team-mode-${o.k}`}
                  onClick={() => setProjTeamMode(o.k)}
                  className={`px-3 py-2.5 border text-sm transition-colors ${projTeamMode === o.k ? "border-brand bg-brand/[0.05] text-brand font-medium" : "border-neutral-200 hover:border-neutral-400"}`}
                >{o.t}</button>
              ))}
            </div>

            {projTeamMode !== "none" && (
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-[12px] text-ink-muted">Alocat (%) / persoană</label>
                  <input
                    type="number" step="5" min="0" max="100" value={projTeamHours}
                    onChange={(e) => setProjTeamHours(e.target.value)}
                    data-testid="proj-team-hours"
                    className="w-24 px-2 py-1.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm font-mono" />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[12px] text-ink-muted">Perioadă</label>
                  <input type="date" value={projTeamStart} onChange={(e) => setProjTeamStart(e.target.value)}
                    data-testid="proj-team-start" disabled={projTeamPermanent}
                    className="px-2 py-1.5 border border-neutral-200 focus:border-brand focus:outline-none text-[12px] disabled:opacity-40 disabled:bg-neutral-50" />
                  <span className="text-ink-muted text-xs">→</span>
                  <input type="date" value={projTeamEnd} onChange={(e) => setProjTeamEnd(e.target.value)}
                    data-testid="proj-team-end" disabled={projTeamPermanent}
                    className="px-2 py-1.5 border border-neutral-200 focus:border-brand focus:outline-none text-[12px] disabled:opacity-40 disabled:bg-neutral-50" />
                </div>
                <label className="flex items-center gap-1.5 text-[12px] text-ink-muted cursor-pointer">
                  <input type="checkbox" checked={projTeamPermanent}
                    data-testid="proj-team-permanent"
                    onChange={(e) => setProjTeamPermanent(e.target.checked)} />
                  Alocare permanentă
                </label>
              </div>
            )}

            {projTeamMode === "all" && (
              <div className="mt-2 text-[12px] text-ink-muted">
                Toți angajații activi vor fi alocați pe proiect. Rolul se preia automat din funcția fiecăruia.
              </div>
            )}

            {projTeamMode === "some" && (
              <div className="mt-3">
                {employees.filter(e => e.is_active !== false).length === 0 ? (
                  <div className="text-[12px] text-ink-muted">Nu ai acces la lista de angajați.</div>
                ) : (
                  <>
                    <button
                      type="button"
                      data-testid="proj-team-select-all"
                      onClick={() => {
                        const avail = employees.filter(e => e.is_active !== false);
                        setProjTeamSelected(prev => prev.size === avail.length && avail.length > 0 ? new Set() : new Set(avail.map(e => e.id)));
                      }}
                      className="mb-2 flex items-center gap-2 text-sm text-brand hover:text-brand-hover font-medium"
                    >
                      <span className={`h-4 w-4 border flex items-center justify-center ${projTeamSelected.size === employees.filter(e => e.is_active !== false).length && projTeamSelected.size > 0 ? "bg-brand border-brand" : "border-neutral-300"}`}>
                        {projTeamSelected.size === employees.filter(e => e.is_active !== false).length && projTeamSelected.size > 0 && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                      </span>
                      Selectează toți ({projTeamSelected.size} aleși)
                    </button>
                    <div className="max-h-[220px] overflow-y-auto border border-neutral-200 divide-y divide-neutral-100">
                      {employees.filter(e => e.is_active !== false).map((e) => {
                        const on = projTeamSelected.has(e.id);
                        return (
                          <button
                            key={e.id}
                            type="button"
                            data-testid={`proj-team-emp-${e.id}`}
                            onClick={() => setProjTeamSelected(prev => {
                              const n = new Set(prev);
                              if (n.has(e.id)) n.delete(e.id); else n.add(e.id);
                              return n;
                            })}
                            className={`w-full text-left flex items-center gap-3 px-3 py-2.5 transition-colors ${on ? "bg-brand/[0.04]" : "hover:bg-ink-soft"}`}
                          >
                            <span className={`shrink-0 h-4 w-4 border flex items-center justify-center ${on ? "bg-brand border-brand" : "border-neutral-300"}`}>
                              {on && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                            </span>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-ink truncate">{e.name}</div>
                              <div className="text-[12px] text-ink-muted truncate">{e.job_title || e.role}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button onClick={() => setNewProject(false)} className="px-4 py-2.5 border border-neutral-200 hover:border-ink text-sm">Renunță</button>
            <button data-testid="proj-save" onClick={createProject} disabled={projBusy} className="btn-brand disabled:opacity-60">
              {projBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Se creează</> : <><Plus className="h-4 w-4" /> Creează proiect</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Financials Modal */}
      <Dialog open={!!finProject} onOpenChange={(o) => !o && setFinProject(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
              <Wallet className="h-6 w-6 text-brand" /> Setări finanțe proiect
            </DialogTitle>
            <DialogDescription>
              {finProject?.code} · {finProject?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <FinField label="Buget ore (h)" value={finForm.budget_hours} onChange={(v) => setFinForm({ ...finForm, budget_hours: v })} testid="fin-hours" />
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Buget total (€)</label>
              <div className="mt-1 w-full px-3 py-2.5 border border-neutral-100 bg-ink-soft font-mono text-sm" data-testid="fin-budget-readonly">
                {(finProject?.budget_amount || 0).toLocaleString("ro-RO")} €
                <span className="text-[11px] text-ink-muted font-sans ml-2">— se setează pe perioade din „Editează”</span>
              </div>
            </div>
            <FinField label="Alte cheltuieli directe – sumă fixă (€)" value={finForm.direct_expenses} onChange={(v) => setFinForm({ ...finForm, direct_expenses: v })} testid="fin-direct" />
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Cheltuieli indirecte (%)</label>
              <input data-testid="fin-indirect" type="number" step="any" placeholder="gol = procentul global din Setări P&L"
                value={finForm.indirect_expenses_pct}
                onChange={(e) => setFinForm({ ...finForm, indirect_expenses_pct: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none font-mono" />
            </div>
          </div>
          <div className="mt-3 p-3 bg-ink-soft text-xs text-ink-muted">
            Cheltuielile indirecte (regie de proiect) se aplică ca procent peste costul manoperei. Lasă câmpul gol ca să folosești procentul global din Setări P&amp;L. Cheltuielile directe datate (subcontractori, materiale, deconturi) se gestionează din modulul P&amp;L.
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <button onClick={() => setFinProject(null)} className="px-4 py-2.5 border border-neutral-200 hover:border-ink text-sm">Renunță</button>
            <button data-testid="fin-save" onClick={saveFinancials} disabled={finBusy} className="btn-brand disabled:opacity-60">
              {finBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Se salvează</> : <><Save className="h-4 w-4" /> Salvează</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Project Modal */}
      <Dialog open={!!editProject} onOpenChange={(o) => !o && setEditProject(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
              <Pencil className="h-6 w-6 text-brand" /> Editează proiect
            </DialogTitle>
            <DialogDescription>
              {editProject?.code} · Modifică detaliile proiectului.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <ProjField label="Nume proiect *" value={editForm.name} onChange={(v) => setEditForm({ ...editForm, name: v })} testid="edit-proj-name" />
            <ProjField label="Cod proiect *" value={editForm.code} onChange={(v) => setEditForm({ ...editForm, code: v })} testid="edit-proj-code" placeholder="BRP-2026-001" />
            <ProjField label="Cod SAGA" value={editForm.saga_code} onChange={(v) => setEditForm({ ...editForm, saga_code: v })} testid="edit-proj-saga" placeholder="Cod contabil SAGA (doar în export)" />
            <ProjField label="Client" value={editForm.client_name} onChange={(v) => setEditForm({ ...editForm, client_name: v })} testid="edit-proj-client" placeholder="Nume client" />
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Status</label>
              <select data-testid="edit-proj-status" value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 bg-white focus:border-brand focus:outline-none text-sm">
                <option value="planning">Planificare</option>
                <option value="active">Activ</option>
                <option value="in_progress">În desfășurare</option>
                <option value="completed">Finalizat</option>
              </select>
            </div>
            <ProjField label="Locație" value={editForm.location} onChange={(v) => setEditForm({ ...editForm, location: v })} testid="edit-proj-location" />
            <ProjField label="Sector" value={editForm.sector} onChange={(v) => setEditForm({ ...editForm, sector: v })} testid="edit-proj-sector" placeholder="Rezidențial, Office..." />
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Data început proiect</label>
              <input data-testid="edit-proj-start" type="date" value={editForm.start_date}
                onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Data sfârșit proiect</label>
              <input data-testid="edit-proj-end" type="date" value={editForm.end_date}
                onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm" />
            </div>
            <div className="col-span-2 border-t border-neutral-100 pt-4">
              <BudgetPeriodsEditor value={editForm.budget_periods} onChange={(v) => setEditForm({ ...editForm, budget_periods: v })}
                projectStart={editForm.start_date} projectEnd={editForm.end_date} />
            </div>
            {(editForm.budget_periods || []).length === 0 && (
              <ProjField label="Buget total (€) — fără perioade" type="number" value={editForm.budget_amount} onChange={(v) => setEditForm({ ...editForm, budget_amount: v })} testid="edit-proj-budget" />
            )}
            <ProjField label="Buget ore (h)" type="number" value={editForm.budget_hours} onChange={(v) => setEditForm({ ...editForm, budget_hours: v })} testid="edit-proj-hours" />
            <div className="col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Descriere</label>
              <textarea data-testid="edit-proj-desc" rows={3} value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm" />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <button onClick={() => setEditProject(null)} className="px-4 py-2.5 border border-neutral-200 hover:border-ink text-sm">Renunță</button>
            <button data-testid="edit-proj-save" onClick={saveEdit} disabled={editBusy} className="btn-brand disabled:opacity-60">
              {editBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Se salvează</> : <><Save className="h-4 w-4" /> Salvează</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}

function FinField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{label}</label>
      <input
        data-testid={testid}
        type="number" step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none font-mono"
      />
    </div>
  );
}

function ProjField({ label, value, onChange, testid, type = "text", placeholder = "" }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{label}</label>
      <input
        data-testid={testid}
        type={type}
        step={type === "number" ? "any" : undefined}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full px-3 py-2.5 border border-neutral-200 focus:border-brand focus:outline-none text-sm ${type === "number" ? "font-mono" : ""}`}
      />
    </div>
  );
}
